// App shell: auth gate → login screen, otherwise topbar + routed page + popups
// (video greeting after a QR card scan, welcome on first visit). Nothing behind
// the gate renders (or is fetched) until /api/auth/me confirms the guest cookie.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';
import { Link, navigate, usePath } from './router.jsx';
import { Toasts } from './components/toast.jsx';
import UploadCta from './components/uploadcta.jsx';
import Greeting from './components/greeting.jsx';
import Welcome, { WELCOME_KEY } from './components/welcome.jsx';
import { HeartIcon, PlayIcon } from './components/icons.jsx';
import { GREETING } from './text.js';
import Login from './pages/login.jsx';
import Home from './pages/home.jsx';
import Gallery from './pages/gallery.jsx';
import Admin from './pages/admin.jsx';
import NotFound from './pages/notfound.jsx';
import Forbidden from './pages/forbidden.jsx';

function hasSeenWelcome() {
	try {
		return window.localStorage.getItem(WELCOME_KEY) === '1';
	} catch {
		return true;
	}
}

// QR code on the printed thank-you cards: /galerie/fotograf?k=<secret>. The key is
// stripped from the address bar before anything renders, so a URL a guest copies or
// bookmarks never carries it, then traded for the guest cookie. Memoized at module
// level so the key is redeemed exactly once however often the boot effect runs.
// Resolves true when a card key was traded successfully.
let linkRedeem = null;
function redeemLinkKey() {
	if (linkRedeem) return linkRedeem;
	const params = new URLSearchParams(window.location.search);
	const key = params.get('k');
	if (!key) {
		linkRedeem = Promise.resolve(false);
		return linkRedeem;
	}
	params.delete('k');
	const qs = params.toString();
	const { hash, pathname } = window.location;
	window.history.replaceState(window.history.state, '', `${pathname}${qs ? `?${qs}` : ''}${hash}`);
	// An invalid or rotated key just falls through to the password screen.
	linkRedeem = api.linkLogin(key).then(
		() => true,
		() => false,
	);
	return linkRedeem;
}

// A card points at a fixed gallery. While that one is unreleased, or not part of the
// card at all, the guest lands on the home page rather than "not found"/"forbidden".
function leaveUnviewableGallery() {
	const slug = window.location.pathname.match(/^\/galerie\/([a-z0-9-]+)$/)?.[1];
	if (!slug) return Promise.resolve();
	return api.categories().then(
		(cats) => {
			if (!cats.some((c) => c.id === slug)) navigate('/', { replace: true });
		},
		() => {},
	);
}

export default function App() {
	const path = usePath();
	const [auth, setAuth] = useState(null); // null = checking, else {guest, admin}
	const [popup, setPopup] = useState(null); // null | 'greeting' | 'welcome'
	// Church-card guests: the gallery ids they may see, else null. Home lists exactly
	// those (the server filters the categories), every other page is refused.
	const only = auth?.only ?? null;
	const [hasGreeting, setHasGreeting] = useState(false);

	useEffect(() => {
		let cancelled = false;
		let linked = false;
		redeemLinkKey()
			.then((ok) => {
				linked = ok;
				return api.me();
			})
			.then((me) => (linked && me.guest ? leaveUnviewableGallery().then(() => me) : me))
			.then((me) => {
				if (cancelled) return;
				setAuth(me);
				if (!me.guest) return;
				// Every card scan opens the video greeting; the welcome waits until it closes.
				if (linked) setPopup('greeting');
				else if (!hasSeenWelcome()) setPopup('welcome');
			})
			.catch(() => {
				if (!cancelled) setAuth({ guest: false, admin: false });
			});
		return () => {
			cancelled = true;
		};
	}, []);

	// The greeting only opens by itself right after a card scan. Whoever may see its
	// gallery (the server filters the list) keeps a way back to it in the topbar.
	const isGuest = !!auth?.guest;
	useEffect(() => {
		if (!isGuest) return undefined;
		let cancelled = false;
		api.categories().then(
			(cats) => {
				if (!cancelled) setHasGreeting(cats.some((c) => c.id === GREETING.category && c.count > 0));
			},
			() => {},
		);
		return () => {
			cancelled = true;
		};
	}, [isGuest]);

	const onLogin = () => {
		setAuth((a) => ({ ...(a ?? {}), guest: true }));
		if (!hasSeenWelcome()) setPopup('welcome');
	};

	// Stable, because Modal re-runs its focus handling whenever onClose changes.
	const closeGreeting = useCallback(() => setPopup(hasSeenWelcome() ? null : 'welcome'), []);
	const closeWelcome = useCallback(() => setPopup(null), []);

	// Checking the cookie — a blank warm page beats a login flash for returning guests.
	if (auth === null) {
		return <div className='boot' aria-hidden='true' />;
	}

	if (!auth.guest) {
		return (
			<>
				<Login onLogin={onLogin} />
				<Toasts />
			</>
		);
	}

	let page;
	const galleryMatch = path.match(/^\/galerie\/([a-z0-9-]+)$/);
	// The banner pushes guests toward uploading while they browse galleries. Home
	// has its own prominent upload card, the upload page has the uploader itself.
	// Limited guests can't upload at all.
	const showCta = !only && path !== '/' && path !== '/galerie/gaestemedien' && path !== '/admin';
	if (only && path !== '/' && !(galleryMatch && only.includes(galleryMatch[1]))) {
		page = <Forbidden />;
	} else if (path === '/') {
		page = <Home />;
	} else if (galleryMatch) {
		page = <Gallery slug={galleryMatch[1]} />;
	} else if (path === '/admin') {
		page = <Admin auth={auth} onAuthChange={setAuth} />;
	} else {
		page = <NotFound />;
	}

	return (
		<>
			<header className='topbar'>
				<Link to='/' className='topbar__brand'>
					<span className='topbar__heart' aria-hidden='true'>
						<HeartIcon size={22} />
					</span>
					<span className='topbar__brand-script'>Herzensmomente</span>
				</Link>
				{hasGreeting && (
					<button type='button' className='topbar__greeting' onClick={() => setPopup('greeting')}>
						<PlayIcon size={16} />
						{GREETING.reopen}
					</button>
				)}
			</header>
			<main className='main'>
				{showCta && <UploadCta />}
				{page}
			</main>
			<footer className='footer'>
				<span className='footer__heart' aria-hidden='true'>
					<HeartIcon size={16} />
				</span>
				<span>Danke, dass ihr mit uns gefeiert habt.</span>
			</footer>
			{popup === 'greeting' && <Greeting onClose={closeGreeting} />}
			{popup === 'welcome' && <Welcome limited={!!only} onClose={closeWelcome} />}
			<Toasts />
		</>
	);
}
